class CustomSelect extends HTMLElement {
  constructor() {
    super();
    this.selectElement = this.querySelector('select');
    this.selectWrapper = this.querySelector('.custom-select-wrapper');
    this.selectedText = this.querySelector('.selected-text');
    this.optionsList = this.querySelector('.options-list');
    this.isOpen = false;
    this.searchEnabled = this.hasAttribute('data-searchable');
    this.showFlags = this.hasAttribute('data-flag');

    if (this.searchEnabled) {
      this.setupSearch();
    }

    this.setupEventListeners();
    this.updateSelectedText();

    // Add initial check for language select
    if (this.selectElement.matches('[data-language-select]')) {
      this.checkLanguageOptionsVisibility();
    }
  }

  setupEventListeners() {
    this.selectWrapper.addEventListener('click', () => this.toggleDropdown());

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.closeDropdown();
      }
    });

    const options = this.querySelectorAll('.select-option');
    options.forEach((option) => {
      option.addEventListener('click', (e) => {
        this.handleOptionSelect(e.target, e);
      });
    });
  }

  setupSearch() {
    // Create and insert search input
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.className =
      'tw-w-full p2 tw-px-sm tw-py-xs tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-bg-white tw-z-10';
    this.searchInput.style.position = 'sticky';
    this.searchInput.style.top = '0';
    this.searchInput.style.marginBottom = '5px';
    this.searchInput.placeholder = 'Search...';
    this.optionsList.insertBefore(this.searchInput, this.optionsList.firstChild);

    // Add search event listener
    this.searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));

    // Prevent search input from closing dropdown
    this.searchInput.addEventListener('click', (e) => e.stopPropagation());

    // Create no results message element with customizable text
    this.noResultsMessage = document.createElement('div');
    this.noResultsMessage.className = 'tw-px-sm p2 tw-py-xs tw-text-gray-500 tw-text-center tw-hidden';
    this.noResultsMessage.textContent = this.getAttribute('data-no-results-text') || 'No matches found';
    this.optionsList.appendChild(this.noResultsMessage);
  }

  handleSearch(query) {
    const options = this.querySelectorAll('.select-option');
    const normalizedQuery = query.toLowerCase();
    let hasVisibleOptions = false;

    options.forEach((option) => {
      const text = option.textContent.toLowerCase();
      const isVisible = text.includes(normalizedQuery);
      option.style.display = isVisible ? '' : 'none';
      if (isVisible) hasVisibleOptions = true;
    });

    // Toggle no results message
    this.noResultsMessage.classList.toggle('tw-hidden', hasVisibleOptions);
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
    this.optionsList.classList.toggle('tw-hidden');
    this.selectWrapper.setAttribute('aria-expanded', this.isOpen);
    if (this.searchEnabled && this.isOpen) {
      this.searchInput.value = '';
      this.handleSearch('');
      // Focus search input when opening
      setTimeout(() => this.searchInput.focus(), 0);
    }
  }

  closeDropdown() {
    this.isOpen = false;
    this.optionsList.classList.add('tw-hidden');
    this.selectWrapper.setAttribute('aria-expanded', 'false');
    this.optionsList.scrollTop = 0;
    if (this.searchEnabled) {
      this.searchInput.value = '';
      this.handleSearch('');
    }
  }

  handleOptionSelect(option, event) {
    const value = option.dataset.value;
    this.selectElement.value = value;
    this.updateSelectedText(option);

    // If this is a country select, update the language options
    if (this.selectElement.matches('[data-country-select]')) {
      // Find all language selects (in case there are multiple on the page)
      const languageSelects = document.querySelectorAll('custom-select select[data-language-select]');
      languageSelects.forEach((languageSelect) => {
        const customLanguageSelect = languageSelect.closest('custom-select');
        if (customLanguageSelect) {
          customLanguageSelect.updateLanguageOptions(value);
        }
      });
    }

    this.closeDropdown();
    event.stopPropagation();

    const changeEvent = new Event('change', { bubbles: true });
    this.selectElement.dispatchEvent(changeEvent);
  }

  updateSelectedText(selectedOption = null) {
    const option = selectedOption || this.optionsList.querySelector(`[data-value="${this.selectElement.value}"]`);
    if (!option) return;

    if (this.showFlags) {
      // Find the img element within the option
      const flagImg = option.querySelector('img');
      const existingSelectedFlag = this.selectedText.querySelector('img');

      if (flagImg) {
        if (existingSelectedFlag) {
          existingSelectedFlag.src = flagImg.src;
        } else {
          // Clone the flag image and insert it before the text
          const newFlag = flagImg.cloneNode(true);
          this.selectedText.insertBefore(newFlag, this.selectedText.firstChild);
        }
      }

      // Create or find the span for the country name
      let textSpan = this.selectedText.querySelector('.selected-country-name');
      if (!textSpan) {
        textSpan = document.createElement('span');
        textSpan.className = 'selected-country-name';
        this.selectedText.appendChild(textSpan);
      }

      // Update the text content
      textSpan.textContent = option.textContent.trim();
    } else {
      this.selectedText.textContent = option.textContent.trim();
    }
  }

  checkLanguageOptionsVisibility() {
    const customOptions = this.querySelectorAll('.select-option');
    const visibleOptions = Array.from(customOptions).filter((option) => !option.classList.contains('tw-hidden'));

    const multiOptionsWrapper = this.closest('.multi-options-wrapper');
    const singleOptionWrapper =
      this.closest('custom-select').parentElement.querySelector('.single-option-input-wrapper');

    if (visibleOptions.length === 1) {
      if (multiOptionsWrapper) multiOptionsWrapper.classList.add('tw-hidden');
      if (singleOptionWrapper) singleOptionWrapper.classList.remove('tw-hidden');
    } else {
      if (multiOptionsWrapper) multiOptionsWrapper.classList.remove('tw-hidden');
      if (singleOptionWrapper) singleOptionWrapper.classList.add('tw-hidden');
    }
  }

  updateLanguageOptions(countryCode) {
    // Only proceed if this is a language select
    if (!this.selectElement.matches('[data-language-select]')) return;

    const germanSpeakingCountries = ['DE', 'CH', 'LI', 'AT'];
    const showGerman = germanSpeakingCountries.includes(countryCode);

    // Update native select options
    const selectOptions = this.selectElement.querySelectorAll('option');
    selectOptions.forEach((option) => {
      if (option.value === 'de') {
        if (showGerman) {
          option.removeAttribute('hidden');
        } else {
          option.setAttribute('hidden', '');
        }
      }
    });

    // Update custom select options
    const customOptions = this.querySelectorAll('.select-option');
    customOptions.forEach((option) => {
      if (option.dataset.value === 'de') {
        if (showGerman) {
          option.classList.remove('tw-hidden');
        } else {
          option.classList.add('tw-hidden');
        }

        // If German is being hidden and it's currently selected, switch to English
        if (!showGerman && this.selectElement.value === 'de') {
          const defaultOption =
            this.querySelector('.select-option[data-value="en"]') ||
            this.querySelector('.select-option:not([data-value="de"])');
          if (defaultOption) {
            this.handleOptionSelect(defaultOption, new Event('click'));
          }
        }
      }
    });

    // Check visibility after updating options
    this.checkLanguageOptionsVisibility();

    // If German should be shown and no language is currently selected, update the display
    if (showGerman) {
      this.updateSelectedText();
    }
  }
}

customElements.define('custom-select', CustomSelect);
